<template>
  <section class="bg-white py-16 md:py-24">
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      
      <div class="text-center">
        <p class="text-sm font-bold text-orange-500 uppercase tracking-wider">
          Porque escolher a OverX?
        </p>
        <h2 class="mt-2 text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900">
          Transforme seu <span class="text-orange-500">Negócio</span> com Resultados Reais
        </h2>
        <p class="mt-4 max-w-3xl mx-auto text-lg text-gray-600">
          Nossas soluções são desenvolvidas para gerar impacto direto no seu faturamento, com foco em resultados mensuráveis e rápido retorno sobre investimento.
        </p>
      </div>

      <div class="mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
        <div v-for="card in cards" :key="card.title" class="bg-white border border-gray-200 rounded-2xl shadow-lg p-8 flex flex-col">
          <div class="flex-shrink-0">
            <span class="inline-flex items-center justify-center h-12 w-12 rounded-lg bg-orange-100">
              <svg class="h-6 w-6 text-orange-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" v-html="card.icon"></svg>
            </span>
          </div>
          <div class="flex-grow">
            <h3 class="mt-6 text-xl font-bold text-gray-900">{{ card.title }}</h3>
            <p class="mt-4 text-base text-gray-600">{{ card.description }}</p>
            <ul class="mt-6 space-y-2">
              <li v-for="item in card.features" :key="item" class="flex items-start">
                <svg class="flex-shrink-0 h-5 w-5 text-orange-500 mt-0.5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
                </svg>
                <span class="ml-3 text-gray-600">{{ item }}</span>
              </li>
            </ul>
          </div>
        </div>
      </div>

      <div class="mt-16 bg-black text-white rounded-2xl p-8 md:p-12">
        <div class="flex flex-col md:flex-row items-center gap-8">
          <div class="flex-shrink-0">
            <div class="bg-gray-800 p-4 rounded-full">
              <div class="bg-orange-500/20 p-3 rounded-full">
                <span class="inline-flex items-center justify-center h-12 w-12 rounded-full bg-orange-500">
                   <svg class="h-7 w-7 text-white" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </span>
              </div>
            </div>
          </div>
          <div class="flex-grow text-center md:text-left">
            <h3 class="text-2xl font-bold">Nossa Garantia de Resultados</h3>
            <p class="mt-2 text-gray-300">
              Estamos tão confiantes em nossas soluções que oferecemos uma garantia única: se você não obtiver resultados mensuráveis em 90 dias, pode cancelar o contrato sem fidelidade.
            </p>
            <div class="mt-6 flex flex-wrap justify-center md:justify-start gap-2">
              <span class="text-xs font-medium bg-green-200 text-green-800 px-3 py-1.5 rounded-full">Sem contratos longos</span>
              <span class="text-xs font-medium bg-green-200 text-green-800 px-3 py-1.5 rounded-full">Resultados mensuráveis</span>
              <span class="text-xs font-medium bg-green-200 text-green-800 px-3 py-1.5 rounded-full">Satisfação garantida</span>
            </div>
            <div class="mt-6">
              <router-link to="/planos" class="inline-block bg-orange-500 text-white font-bold py-3 px-8 rounded-lg hover:bg-orange-600 transition">
                QUERO GARANTIR MEUS RESULTADOS
              </router-link>
            </div>
          </div>
        </div>
      </div>

    </div>
  </section>
</template>

<script setup>
import { ref } from 'vue';

const cards = ref([
  {
    title: 'Aumento de Faturamento',
    description: 'Nossos clientes registram um aumento médio de 127% no faturamento nos primeiros 6 meses de implementação, podendo chegar a 247%.',
    features: ['Aumento de ticket médio', 'Mais vendas recorrentes', 'ROI comprovado'],
    icon: `<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />`
  },
  {
    title: 'Automação de Processos',
    description: 'Reduza custos operacionais em até 65% com nossas soluções de automação, liberando sua equipe para focar no crescimento.',
    features: ['Redução de 70% em erros', 'Economia de 30h/semana', 'Escalabilidade garantida'],
    icon: `<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />`
  },
  {
    title: 'Experiência do Cliente',
    description: 'Aumente a satisfação e fidelização dos clientes com interfaces intuitivas e processos otimizados que geram mais vendas.',
    features: ['+89% em recompras', '-42% em abandono', 'NPS acima de 85'],
    icon: `<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 20h5.423a2 2 0 001.94-2.512l-1.41-4.23a2 2 0 00-1.94-1.488H13.5A2.5 2.5 0 0111 9.5V5.5a2.5 2.5 0 012.5-2.5h1A2.5 2.5 0 0117 5.5v1.5M4 20h5.423a2 2 0 001.94-2.512l-1.41-4.23a2 2 0 00-1.94-1.488H3.5A2.5 2.5 0 011 9.5V5.5a2.5 2.5 0 012.5-2.5h1A2.5 2.5 0 017 5.5v1.5" />`
  }
]);
</script>